import { useState } from 'react';

import type { SuggestionView, TicketSuggestions } from '@nightshift/ui-types';

import { api, ApiError } from '../../live/api.ts';
import { useTimeFormat } from '../../lib/display.ts';
import { observedTime } from '../../lib/format.ts';
import { dependencyDirection } from '../../lib/dependency-direction.ts';

/** Dependencies needing a decision, advisory shared edits, and the decision history. */
export function SuggestionsPanel({
  ticketKey,
  suggestions,
  now,
  onReloaded,
  onPreviewTicket,
}: {
  ticketKey: string;
  suggestions: TicketSuggestions;
  now: number;
  onReloaded: () => Promise<void> | void;
  onPreviewTicket?: (key: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Accept, Flip and Dismiss announce themselves by changing the list. A rescan changes
  // nothing on screen -- the comparison happens later -- so without a word it reads as a
  // button that does nothing, and people press it again.
  const [notice, setNotice] = useState<string | null>(null);
  const [showPossible, setShowPossible] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const format = useTimeFormat();

  async function run(work: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await work();
      await onReloaded();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 409
          ? 'That suggestion changed while you were reading it. It has been reloaded.'
          : err instanceof Error
            ? err.message
            : String(err),
      );
      if (err instanceof ApiError && err.status === 409) await onReloaded();
    } finally {
      setBusy(false);
    }
  }

  const decide = (
    row: SuggestionView,
    action: 'accept' | 'flip' | 'dismiss' | 'acknowledge' | 'convert',
    extra: { dependentKey?: string; blockerKey?: string } = {},
  ) =>
    void run(() =>
      api.decideSuggestion(ticketKey, row.id, action, {
        expectedInputRevision: row.inputRevision,
        ...extra,
      }),
    );

  const attention = suggestions.suggested.filter((row) => row.direction !== 'overlaps');
  const overlaps = [...suggestions.suggested, ...suggestions.possible].filter((row) => row.direction === 'overlaps');
  const possible = suggestions.possible.filter((row) => row.direction !== 'overlaps');

  return (
    <section className="sugg" aria-label="Suggested relationships">
      <div className="sugg-head">
        <h3 className="deps-heading">Needs attention</h3>
        <span className="sugg-status dim">{suggestions.scanStatus.detail}</span>
      </div>
      <p className="deps-empty">
        High-confidence dependencies are added automatically when safe. These exceptions need a decision; medium-confidence findings remain advisory.
      </p>

      {error ? <p className="deps-error">{error}</p> : null}
      {notice ? <p className="deps-empty">{notice}</p> : null}

      {attention.length === 0 ? (
        <p className="deps-empty">No relationship decisions needed.</p>
      ) : (
        <ul className="sugg-list">
          {attention.map((row) => (
            <SuggestionRow
              key={row.id}
              row={row}
              ticketKey={ticketKey}
              busy={busy}
              now={now}
              onDecide={decide}
              onPreviewTicket={onPreviewTicket}
            />
          ))}
        </ul>
      )}

      {overlaps.length > 0 ? (
        <div className="sugg-group" aria-label="Shared edits">
          <h3 className="deps-heading">Shared edits <span className="num dim">{overlaps.length}</span></h3>
          <p className="deps-empty">Either ticket can go first. Starting work automatically queues high-confidence overlaps behind work already in flight. Medium-confidence overlaps are advisory.</p>
          <ul className="sugg-list">
            {overlaps.map((row) => <SuggestionRow key={row.id} row={row} ticketKey={ticketKey} busy={busy} now={now} onDecide={decide} onPreviewTicket={onPreviewTicket} />)}
          </ul>
        </div>
      ) : null}

      {possible.length > 0 ? (
        <div className="sugg-group">
          <button
            type="button"
            className="sugg-toggle"
            aria-expanded={showPossible}
            onClick={() => setShowPossible((prev) => !prev)}
          >
            Possible <span className="num dim">{possible.length}</span>
          </button>
          {showPossible ? (
            <ul className="sugg-list">
              {possible.map((row) => (
                <SuggestionRow
                  key={row.id}
                  row={row}
                  ticketKey={ticketKey}
                  busy={busy}
                  now={now}
                  onDecide={decide}
                  onPreviewTicket={onPreviewTicket}
                />
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {suggestions.history.length > 0 ? (
        <div className="sugg-group">
          <button
            type="button"
            className="sugg-toggle"
            aria-expanded={showHistory}
            onClick={() => setShowHistory((prev) => !prev)}
          >
            History <span className="num dim">{suggestions.history.length}</span>
          </button>
          {showHistory ? (
            <ul className="sugg-list is-history">
              {suggestions.history.map((row) => (
                <li key={row.id} className="sugg-row is-settled">
                  <span className="sugg-relationship">
                    Proposed: {dependencyDirection(ticketKey, row.otherTicketKey, row.direction).relationship}
                  </span>
                  <span className="clamp-1 dim">{row.otherTitle}</span>
                  <span className="chip sugg-chip">{row.decision ?? row.status}</span>
                  <span className="dim">{observedTime(row.updatedAt, now, format)}</span>
                  <p className="sugg-history-reason dim">
                    {suggestions.decisions.find((entry) => entry.suggestionId === row.id)?.reason ?? row.reason}
                  </p>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      <div className="sugg-foot">
        <button
          type="button"
          className="btn btn-sm"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              const result = await api.rescanTicket(ticketKey);
              setNotice(result.message);
            })
          }
        >
          Rescan
        </button>
        <span className="deps-add-hint">
          Regenerates the footprint, then dependencies. Resets failed retries; budget and queue limits still apply.
        </span>
      </div>
    </section>
  );
}

function SuggestionRow({
  row,
  ticketKey,
  busy,
  now,
  onDecide,
  onPreviewTicket,
}: {
  row: SuggestionView;
  ticketKey: string;
  busy: boolean;
  now: number;
  onDecide: (
    row: SuggestionView,
    action: 'accept' | 'flip' | 'dismiss' | 'acknowledge' | 'convert',
    extra?: { dependentKey?: string; blockerKey?: string },
  ) => void;
  onPreviewTicket?: (key: string) => void;
  }) {
  const format = useTimeFormat();
  const overlap = row.direction === 'overlaps';
  const direction = dependencyDirection(ticketKey, row.otherTicketKey, row.direction);
  return (
    <li className={`sugg-row ${overlap ? 'is-overlap' : `is-${row.band}`}${row.staleInput ? ' is-stale' : ''}`}>
      <div className="sugg-row-head">
        <span className="sugg-relationship">{direction.relationship}</span>
        <span className={`chip chip-band-${row.band}`}>{row.band}</span>
        <span className="dim">{observedTime(row.updatedAt, now, format)}</span>
      </div>
      <p className="sugg-order">{direction.order}</p>
      <p className="sugg-other dim">{row.otherTicketKey}: {row.otherTitle}</p>
      <p className="sugg-reason">{row.reason}</p>
      {row.automationException ? <p className="sugg-stale-note">{row.automationException}</p> : null}
      {row.evidence.length > 0 ? (
        <ul className="sugg-evidence">
          {row.evidence.slice(0, 4).map((line) => (
            <li key={line} className="mono">
              {line}
            </li>
          ))}
        </ul>
      ) : null}
      {row.staleInput ? (
        <p className="sugg-stale-note">
          Either ticket has changed since this was worked out. The next scan will revisit it.
        </p>
      ) : null}
      <div className="sugg-actions">
        {onPreviewTicket ? (
          <button
            type="button"
            className="btn btn-sm sugg-preview"
            onClick={() => onPreviewTicket(row.otherTicketKey)}
            aria-label={`Preview ${row.otherTicketKey}: ${row.otherTitle}`}
          >
            Preview {row.otherTicketKey}
          </button>
        ) : null}
        {overlap ? (
          <>
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy}
              onClick={() =>
                onDecide(row, 'convert', {
                  dependentKey: ticketKey,
                  blockerKey: row.otherTicketKey,
                })
              }
            >
              {ticketKey} depends on {row.otherTicketKey}
            </button>
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy}
              onClick={() =>
                onDecide(row, 'convert', {
                  dependentKey: row.otherTicketKey,
                  blockerKey: ticketKey,
                })
              }
            >
              {row.otherTicketKey} depends on {ticketKey}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-sm btn-accent"
              disabled={busy}
              onClick={() => onDecide(row, 'accept')}
              title={`Confirm that ${direction.dependent} depends on ${direction.blocker}`}
            >
              Accept: {direction.blocker} first
            </button>
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy}
              onClick={() => onDecide(row, 'flip')}
              title={`Confirm that ${direction.blocker} depends on ${direction.dependent}`}
            >
              Reverse and accept: {direction.dependent} first
            </button>
          </>
        )}
        <button
          type="button"
          className="btn btn-sm"
          disabled={busy}
          onClick={() => onDecide(row, 'dismiss')}
        >
          Dismiss
        </button>
        {/*
          Acknowledge is an overlap's answer, not a dependency's. An overlap asserts nothing
          about order, so "seen, no action" is a real position; a dependency proposal is
          either right, backwards or wrong, and those are the three buttons above.
        */}
        {overlap ? (
          <button
            type="button"
            className="btn btn-sm"
            disabled={busy}
            onClick={() => onDecide(row, 'acknowledge')}
            title="Allow these tickets to run together"
          >
            Allow parallel work
          </button>
        ) : null}
      </div>
    </li>
  );
}
